import React, { useState, useEffect } from 'react';
import { ShieldCheck, Loader2, AlertCircle, CheckCircle2, DollarSign, X } from 'lucide-react';
import apiService from '../services/api';
import { useTranslation } from 'react-i18next';

export default function InvestmentModal({ isOpen, onClose, plan, onStatusChange }) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (isOpen && plan) {
      setAmount(plan.min_amount ? parseFloat(parseFloat(plan.min_amount).toFixed(2)).toString() : '');
      fetchBalance();
    }
    setError('');
    setSuccess(false);
  }, [isOpen, plan]);

  const fetchBalance = async () => {
    try {
      const response = await apiService.get('/wallets/stats');
      if (response.success) {
        setBalance(response.data.investment_available || 0);
      }
    } catch (err) {
      console.error("Error fetching balance:", err);
    }
  };

  const handleMaxClick = () => {
    let maxAmt = parseFloat(balance);
    if (plan?.max_amount && parseFloat(plan.max_amount) < maxAmt) {
      maxAmt = parseFloat(plan.max_amount);
    }
    // Floor to 2 decimals to avoid rounding issues with input
    setAmount((Math.floor(maxAmt * 100) / 100).toString());
  };

  const handleInvest = async () => {
    if (!amount || parseFloat(amount) < parseFloat(plan.min_amount)) {
      setError(t('investments.modal.min_error', { min: plan.min_amount, symbol: plan.symbol }));
      return;
    }

    if (plan.max_amount && parseFloat(amount) > parseFloat(plan.max_amount)) {
      setError(t('investments.modal.max_error', { max: plan.max_amount, symbol: plan.symbol }));
      return;
    }

    if (parseFloat(amount) > parseFloat(balance)) {
      setError(t('investments.modal.balance_error'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await apiService.post('/investments/invest', {
        plan_id: plan.id,
        amount: parseFloat(amount)
      });

      if (response.success) {
        setSuccess(true);
        if (onStatusChange) onStatusChange();
        setTimeout(() => onClose(), 3000);
      } else {
        setError(response.message || t('investments.modal.error'));
      }
    } catch (err) {
      setError(t('common.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl z-[9999] flex items-end sm:items-center justify-center sm:p-4">
      <div className="glass-card w-full max-w-lg border-white/10 shadow-[0_32px_128px_-16px_rgba(99,102,241,0.2)] overflow-hidden animate-in fade-in zoom-in duration-300 rounded-t-3xl sm:rounded-3xl">
        {!success ? (
          <>
            <div className="p-5 sm:p-10 border-b border-white/5 bg-gradient-to-br from-indigo-500/10 to-transparent relative">
              <div className="flex justify-between items-start mb-4 sm:mb-8">
                <div className="p-3 sm:p-4 bg-indigo-500/10 rounded-2xl sm:rounded-[24px] border border-indigo-500/20 shadow-2xl">
                  <ShieldCheck size={28} className="text-indigo-400" />
                </div>
                <button 
                  onClick={onClose} 
                  className="p-2 bg-white/5 rounded-full text-slate-500 hover:text-white hover:bg-white/10 transition-all"
                >
                  <X size={20} />
                </button>
              </div>
              <h2 className="text-xl sm:text-3xl font-black font-outfit uppercase tracking-tight text-white mb-2 break-words">
                {t('investments.modal.activate')} <span className="text-indigo-400">{plan?.name}</span>
              </h2>
              <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">{ t('investments.modal.secure_allocation')}</p>
            </div>

            <div className="p-5 sm:p-10 space-y-5 sm:space-y-8">
              {error && (
                <div className="p-4 bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center gap-3 text-rose-400 text-xs font-bold">
                  <AlertCircle size={18} className="shrink-0" />
                  <span className="break-words">{error}</span>
                </div>
              )}

              <div className="space-y-4 sm:space-y-6">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-500">{t('investments.modal.investment_amount')}</span>
                  <div className="flex items-center gap-2 px-3 py-1 bg-white/5 rounded-lg border border-white/5">
                    <span className="text-[9px] font-black text-slate-600 uppercase">{t('investments.modal.available')}:</span>
                    <span className="text-[11px] font-black text-indigo-400 font-outfit tracking-wide">{parseFloat(balance).toFixed(2)} {plan?.symbol}</span>
                  </div>
                </div>
                
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 sm:pl-6 flex items-center pointer-events-none text-slate-600 group-focus-within:text-indigo-400 transition-colors">
                    <DollarSign size={20} />
                  </div>
                  <input 
                    type="number" 
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full bg-slate-900/60 border border-white/10 rounded-[28px] pl-12 sm:pl-16 pr-20 py-4 sm:py-6 text-white font-black text-3xl sm:text-4xl font-outfit focus:border-indigo-500/50 outline-none transition-all shadow-inner tabular-nums"
                  />
                  <div className="absolute inset-y-0 right-0 pr-4 sm:pr-8 flex items-center gap-3">
                    <button 
                      onClick={handleMaxClick}
                      className="px-3 py-1 bg-indigo-500/20 hover:bg-indigo-500/30 text-indigo-400 font-black text-[10px] rounded-lg uppercase tracking-widest transition-all border border-indigo-500/30"
                    >
                      Max
                    </button>
                    <span className="text-sm font-black text-slate-700 uppercase tracking-widest pointer-events-none">{plan?.symbol}</span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 sm:gap-6">
                  <div className="p-4 sm:p-5 bg-white/5 border border-white/5 rounded-2xl">
                    <div className="text-[9px] font-black text-slate-600 uppercase mb-2 tracking-widest">{t('investments.cycle')}</div>
                    <div className="text-lg sm:text-xl font-black text-white font-outfit">{plan?.duration_days} <span className="text-[10px] text-slate-500">{t('investments.days')}</span></div>
                  </div>
                  <div className="p-4 sm:p-5 bg-white/5 border border-white/5 rounded-2xl">
                    <div className="text-[9px] font-black text-slate-600 uppercase mb-2 tracking-widest">{t('investments.daily_yield')}</div>
                    <div className="text-lg sm:text-xl font-black text-emerald-400 font-outfit">+{parseFloat(plan?.daily_return_percent || 0).toFixed(2)}%</div>
                  </div>
                </div>
              </div>

              <button 
                onClick={handleInvest}
                disabled={loading}
                className="w-full btn-premium !py-4 sm:!py-6 flex items-center justify-center gap-3 font-black text-xs uppercase tracking-[0.3em] shadow-indigo-500/30 active:scale-95 transition-all"
              >
                {loading ? (
                  <Loader2 size={20} className="animate-spin" />
                ) : (
                  <>
                    {t('investments.modal.confirm')} 
                    <ShieldCheck size={18} />
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <div className="p-20 text-center animate-in zoom-in duration-500 relative">
            <div className="absolute inset-0 bg-emerald-500/5 blur-[120px] rounded-full"></div>
            <div className="relative z-10">
                <div className="w-32 h-32 bg-emerald-500/10 rounded-[48px] flex items-center justify-center mx-auto mb-10 border border-emerald-500/20 shadow-2xl">
                <CheckCircle2 size={64} className="text-emerald-500" />
                </div>
                <h2 className="text-4xl font-black text-white font-outfit mb-6 uppercase tracking-tighter">
                    {t('investments.modal.success_title')}
                </h2>
                <p className="text-slate-400 font-medium mb-0 leading-relaxed text-lg italic">
                "{t('investments.modal.success_desc')}"
                </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
